import { useEffect, useCallback, useRef } from 'react';
import { useAsync, UseAsyncState } from './useAsync';

export interface UseFetchOptions<T> {
  /** Skip fetching (useful for conditional fetching) */
  skip?: boolean;
  /** Called on successful fetch */
  onSuccess?: (data: T) => void;
  /** Called on fetch error */
  onError?: (error: Error) => void;
  /** Default error message */
  defaultErrorMessage?: string;
}

export interface UseFetchReturn<T> extends UseAsyncState<T> {
  /** Re-fetch the data */
  refetch: () => Promise<T | null>;
  /** Manually set data (for optimistic updates) */
  setData: (data: T | null) => void;
}

/**
 * Hook for fetching data with automatic execution on mount/deps change.
 * Built on useAsync but auto-executes when dependencies change.
 *
 * @example
 * const { data, isLoading, error, refetch } = useFetch(
 *   () => api.getBoard(boardId),
 *   [boardId]
 * );
 */
export function useFetch<T>(
  fetchFn: () => Promise<T>,
  deps: React.DependencyList,
  options?: UseFetchOptions<T>
): UseFetchReturn<T> {
  const { skip = false, ...asyncOptions } = options ?? {};
  const { data, isLoading, error, execute, setData } = useAsync(fetchFn, asyncOptions);

  // Track if this is the first render to handle skip properly
  const hasExecutedRef = useRef(false);

  const refetch = useCallback(async () => {
    if (skip) return null;
    return execute();
  }, [execute, skip]);

  // Auto-fetch on mount and when deps change
  useEffect(() => {
    if (skip) {
      hasExecutedRef.current = false;
      return;
    }
    hasExecutedRef.current = true;
    execute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip, ...deps]);

  return {
    data,
    isLoading: skip ? false : isLoading,
    error: skip ? null : error,
    refetch,
    setData,
  };
}

/**
 * Hook for fetching data only when a condition is met.
 * Useful for dependent fetches (fetch B only when A is loaded).
 *
 * @example
 * const { data: board } = useFetch(() => api.getBoard(id), [id]);
 * const { data: squares } = useFetchWhen(
 *   () => api.getSquares(id),
 *   [id],
 *   !!board  // Only fetch when board is loaded
 * );
 */
export function useFetchWhen<T>(
  fetchFn: () => Promise<T>,
  deps: React.DependencyList,
  condition: boolean,
  options?: Omit<UseFetchOptions<T>, 'skip'>
): UseFetchReturn<T> {
  return useFetch(fetchFn, deps, { ...options, skip: !condition });
}
